// src/main.tsx
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthKitProvider, useAuth } from "@workos-inc/authkit-react";
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithAuthKit } from "@convex-dev/workos";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import "./index.css";
import App from "./app/App";

/** Normalize redirect URI to avoid double protocol (e.g. https://https://...) from env/platform. */
function normalizeRedirectUri(uri: string | undefined): string | undefined {
  if (uri == null || uri === "") return uri;
  const trimmed = uri.trim();
  return trimmed.replace(/^(https?):\/\/\1:\/\//, "$1://");
}

// Defer Sentry initialization to reduce initial blocking time
// Initialize after initial render to avoid blocking the main thread
if (import.meta.env.VITE_SENTRY_DSN) {
  // Use requestIdleCallback or setTimeout to defer initialization
  const initSentry = () => {
    import("@sentry/react").then((Sentry) => {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE || "development",
        integrations: [
          Sentry.browserTracingIntegration({
            // Start tracing after initial load
            tracePropagationTargets: ["localhost", /^\//],
          }),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
            // Reduce initial overhead
            networkDetailAllowUrls: [],
            networkRequestSanitizer: () => null,
            networkResponseSanitizer: () => null,
          }),
        ],
        // Performance Monitoring
        tracesSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
        // Session Replay
        replaysSessionSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
        replaysOnErrorSampleRate: 1.0,
        // Defer initial load
        beforeSend: (event) => {
          // Only send events after initial load is complete
          return event;
        },
      });
    });
  };

  // Defer initialization using scheduler API or setTimeout
  if ("requestIdleCallback" in window) {
    requestIdleCallback(initSentry, { timeout: 2000 });
  } else {
    // Fallback: delay by 1 second to allow initial render
    setTimeout(initSentry, 1000);
  }
}

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL);

// Optimize for back/forward cache (bfcache)
// Note: Pages with WebSockets cannot enter bfcache, but we can handle page lifecycle events
// to optimize connection management when navigating away and back
if (typeof window !== 'undefined') {
  // Handle page visibility changes to optimize connection management
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Page is hidden - Convex will handle connection management automatically
      // We can add custom logic here if needed
    } else {
      // Page is visible again - Convex will automatically reconnect
      // This helps when returning from other tabs/apps
    }
  });

  // Handle pagehide event (when navigating away)
  window.addEventListener('pagehide', (event) => {
    // Persisted: true means the page is being cached (bfcache attempt)
    // Note: With WebSockets, bfcache won't work, but this is still useful for cleanup
    if (event.persisted) {
      // Browser attempted to cache the page
      // Convex connections will be closed automatically
    }
  });

  // Handle pageshow event (when returning to page)
  window.addEventListener('pageshow', (event) => {
    // Persisted: true means page was restored from bfcache
    // Note: With WebSockets, this won't happen, but we handle it for completeness
    if (event.persisted) {
      // Page was restored from cache
      // Convex will automatically reconnect WebSocket connections
    } else {
      // Normal page load or refresh
      // Convex connections are already established
    }
  });
}

// Cookiebot is loaded via the script in index.html and manages the banner itself.
// Do not call Cookiebot.show() from app code: on refresh, Cookiebot may not have
// read the consent cookie yet, so hasResponse can be false and calling show() would
// wrongly display the banner again. Let Cookiebot show the banner only when it
// has determined there is no valid consent.

// Use concurrent features for better performance
const rootElement = document.getElementById("root")!;
const root = createRoot(rootElement);

// Render immediately - React 19 concurrent rendering will optimize
// The loading skeleton in HTML will be replaced automatically
root.render(
  <StrictMode>
    <AppErrorBoundary>
      <AuthKitProvider
        clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
        redirectUri={normalizeRedirectUri(import.meta.env.VITE_WORKOS_REDIRECT_URI)}
      >
        <ConvexProviderWithAuthKit client={convex} useAuth={useAuth}>
          <App />
        </ConvexProviderWithAuthKit>
      </AuthKitProvider>
    </AppErrorBoundary>
  </StrictMode>
);
